/**
 * Functionality-driven sidebar nav sections.
 * Renders dynamic sections from FUNCTIONALITY_NAV based on the selected functionality,
 * filtered by user role and IFRS 4 toggle. Also handles the regulator minimal view.
 */

import { Box, Collapse, List } from '@mui/material';
import { Dashboard as DashboardIcon } from '@mui/icons-material';
import { SidebarNavItem, SectionHeader } from '../SidebarNavItem';
import type { NavItem, NavSection } from '../layoutConstants';
import { getIcon } from './iconMap';

interface FunctionalityNavProps {
  sidebarExpanded: boolean;
  isRegulator: boolean;
  sections: NavSection[];
  userRole: string;
  ifrs4Enabled: boolean;
  isSectionOpen: (label: string) => boolean;
  toggleSection: (label: string) => void;
  onMobileNavClick: () => void;
}

export function FunctionalityNav({
  sidebarExpanded,
  isRegulator,
  sections,
  userRole,
  ifrs4Enabled,
  isSectionOpen,
  toggleSection,
  onMobileNavClick,
}: FunctionalityNavProps) {
  /** Filter items by role and IFRS 4 toggle. */
  const filterItems = (items: NavItem[]): NavItem[] =>
    items.filter((item) => {
      if (item.roles && !item.roles.includes(userRole)) return false;
      if (item.ifrs4Only && !ifrs4Enabled) return false;
      return true;
    });

  return (
    <>
      {/* Regulator: minimal static nav */}
      {isRegulator && (
        <>
          <SectionHeader label="Dashboard" sidebarExpanded={sidebarExpanded} />
          <SidebarNavItem
            to="/dashboard"
            label="Dashboard"
            icon={<DashboardIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
        </>
      )}

      {/* Functionality-driven sections (non-admin, non-regulator) */}
      {!isRegulator &&
        sections.map((section) => {
          const visibleItems = filterItems(section.items);
          if (visibleItems.length === 0) return null;

          const open = isSectionOpen(section.label);
          return (
            <Box key={section.label}>
              <SectionHeader
                label={section.label}
                collapsible
                open={open}
                onToggle={() => toggleSection(section.label)}
                sidebarExpanded={sidebarExpanded}
              />
              <Collapse
                in={sidebarExpanded ? open : true}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {visibleItems.map((item) => (
                    <SidebarNavItem
                      key={item.to + item.label}
                      to={item.to}
                      label={item.label}
                      icon={getIcon(item.icon)}
                      indent={section.collapsible}
                      sidebarExpanded={sidebarExpanded}
                      onNavigate={onMobileNavClick}
                    />
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
    </>
  );
}
